import React from "react";
import {
  Box,
  Container,
  Typography,
  GridLegacy as Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import {
  Computer as ComputerIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon,
  Storage as StorageIcon,
  Support as SupportIcon,
  NetworkCheck as NetworkIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function ITSupport() {
  const { t } = useTranslation();
  // Use "itsupport" namespace instead of "home"
  const itsupportT = (key: string) => t(`itsupport:${key}`);

  const services = [
    {
      icon: <ComputerIcon color="primary" fontSize="large" />,
      title: itsupportT("itSupportService1Title"),
      description: itsupportT("itSupportService1Description"),
    },
    {
      icon: <NetworkIcon color="primary" fontSize="large" />,
      title: itsupportT("itSupportService2Title"),
      description: itsupportT("itSupportService2Description"),
    },
    {
      icon: <CloudIcon color="primary" fontSize="large" />,
      title: itsupportT("itSupportService3Title"),
      description: itsupportT("itSupportService3Description"),
    },
  ];

  const benefits = [
    itsupportT("itSupportBenefit1"),
    itsupportT("itSupportBenefit2"),
    itsupportT("itSupportBenefit3"),
    itsupportT("itSupportBenefit4"),
    itsupportT("itSupportBenefit5"),
    itsupportT("itSupportBenefit6"),
  ];

  const faqs = [
    {
      q: itsupportT("itSupportFaq1Question"),
      a: itsupportT("itSupportFaq1Answer"),
    },
    {
      q: itsupportT("itSupportFaq2Question"),
      a: itsupportT("itSupportFaq2Answer"),
    },
    {
      q: itsupportT("itSupportFaq3Question"),
      a: itsupportT("itSupportFaq3Answer"),
    },
    {
      q: itsupportT("itSupportFaq4Question"),
      a: itsupportT("itSupportFaq4Answer"),
    },
  ];

  return (
    <Box sx={{ overflow: "hidden", background: "transparent" }}>
      <Box
        sx={{
          background: `
            linear-gradient(90deg, #f8fafc 0%, transparent 25%),
            linear-gradient(270deg, #f8fafc 0%, transparent 25%),
            linear-gradient(180deg, cadetblue 0%, transparent 80%)
          `,
          backdropFilter: "blur(10px)",
          py: { xs: 3, md: 4 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" fontWeight="bold" sx={{ pb: 2 }}>
            {itsupportT("itSupportHeroTitle")}
          </Typography>
          <Typography
            variant="h6"
            sx={{ mx: "auto", opacity: 0.9, textAlign: "justify" }}
          >
            {itsupportT("itSupportHeroDescription")}
            <Tooltip title="+45 66 77 69 51" placement="top" arrow>
              <Button
                sx={{
                  backgroundColor: "transparent",
                  color: "cadetblue",
                  textTransform: "none",
                  marginBottom: "0px",
                }}
                href={`tel:+4566776951`}
              >
                {itsupportT("callButton")}
              </Button>
            </Tooltip>
            {itsupportT("orText")}
            <Tooltip title={itsupportT("emailTooltip")} placement="top" arrow>
              <Button
                component="a"
                href="/contact"
                sx={{
                  backgroundColor: "transparent",
                  color: "cadetblue",
                  textTransform: "none",
                  marginBottom: "0px",
                }}
              >
                {itsupportT("emailButton")}
              </Button>
            </Tooltip>
            {itsupportT("forQuotationText")}
          </Typography>
        </Container>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": { transform: "translateY(-6px)", boxShadow: 4 },
                }}
              >
                <CardContent sx={{ textAlign: "center" }}>
                  <Box sx={{ mb: 2 }}>{service.icon}</Box>
                  <Typography variant="h5" sx={{ pb: 1 }}>
                    {service.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    textAlign={"center"}
                    
                    pb={2}
                    pl={2}
                    pr={2}
                    lineHeight={1.8}
                  >
                    {service.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Us Section */}
      <Box sx={{ backgroundColor: "transparent", py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
            }}
          >
            {/* Left side: Text */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h3"
                fontWeight="bold"
                sx={{ mb: { xs: 1, md: 2 } }}
              >
                {itsupportT("itSupportWhyChooseTitle")}
              </Typography>

              <List dense>
                {benefits.map((benefit, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Right side: Image */}
            <Box
              sx={{
                flex: 1,
                display: { xs: "none", md: "block" },
              }}
            >
              <img
                src="/logos/ithelp1.jpg"
                alt={itsupportT("itSupportImageAlt")}
                style={{
                  width: "100%",
                  maxWidth: "600px",
                  height: "auto",
                  borderRadius: "12px",
                  background:
                    "linear-gradient(90deg, transparent 0%, cadetblue 50%)",
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
        <Typography
          variant="h4"
          textAlign="center"
          fontWeight="bold"
          gutterBottom
        >
          {itsupportT("itSupportFaqTitle")}
        </Typography>
        <Box sx={{ maxWidth: "800px", mx: "auto", mt: 4 }}>
          {faqs.map((faq, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </Box>
  );
}